import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchRecentSolanaTransactions } from '../api/solana';
import { fetchRecentPrcTransactions } from '../api/transactions';
import { formatRelativeTime } from '../utils/formatTime';

const AUTO_REFRESH_MS = 15_000;

function tag(rows, chain) {
  return rows.map((tx) => ({ ...tx, chain }));
}

export function useRecentAllTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const load = useCallback(async (isInitial) => {
    if (isInitial) setLoading(true);

    try {
      const [prcRows, solRows] = await Promise.all([
        fetchRecentPrcTransactions(1, 50).then((r) => tag(r.transactions || [], 'prc')).catch(() => []),
        fetchRecentSolanaTransactions().then((r) => tag(r, 'solana')).catch(() => []),
      ]);

      if (!mountedRef.current) return;

      const merged = [...prcRows, ...solRows]
        .sort((a, b) => (b.blockTime || 0) - (a.blockTime || 0))
        .slice(0, 50)
        .map((tx) => ({
          ...tx,
          time: tx.blockTime ? formatRelativeTime(tx.blockTime) : '—',
        }));

      setTransactions(merged);
      setError(merged.length === 0 ? 'No recent transactions found.' : null);
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message || 'Failed to load transactions');
      }
    } finally {
      if (mountedRef.current && isInitial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load(true);

    const id = setInterval(() => load(false), AUTO_REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [load]);

  return { transactions, loading, error };
}
